import React, { useEffect } from 'react';
import { RootStateOrAny, useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import InfiniteCalendar, { withRange, Calendar } from 'react-infinite-calendar';
import {
  Paper,
  AppBar,
  Toolbar,
  Grid,
  Typography,
  Switch,
} from '@material-ui/core';
import {
  showHolidays as showHolidaysAction,
  setSelectedRangeDate,
} from './../../redux/actions/newOperationHoursActions';
import './../Fields/Calendar/styles/infiniteCalendarStyles.css';
import './../Fields/Calendar/styles/infiniteCustomStyles.css';
import { localesMap } from './OperationHoursCalendar';
import {
  removeHolidayToolTip,
  removeEventListenersForToolTip,
  createHolidayEvent,
} from '../../utils/utils';
import RBIFollowCursorTooltip from '../RBIFollowCursorTooltip/RBIFollowCursorTooltip';
import { useGeneralTooltip } from '../../utils/tooltipUtils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { debounce } from 'lodash';

const useStyles = RestaurantId =>
  makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
      },
      paperCalendar: {
        textAlign: 'left',
        height: !RestaurantId ? '64.5vh' : '60vh',
        overflow: 'hidden',
      },
      calendarFooter: {
        float: 'right',
      },
      holidayTypography: {
        marginTop: '1%',
        marginLeft: '5%',
        color: 'black',
      },
      holidaySwitch: {
        marginLeft: '25%',
      },
    }),
  );

/**
 * Operation Hours Calendar for Range tab
 * @constructor
 * @returns Range Calendar Component
 */
export default function OperationHoursRangeCalendar({
  RestaurantId,
  TimeZone,
}) {
  const { t } = useTranslation();
  const classes = useStyles(RestaurantId)();
  const dispatch = useDispatch();
  //Value today is important to prevent bug on calendar.
  const today = new Date();

  /** Fetch holidays from reducer */
  const holidays = useSelector(
    (state: RootStateOrAny) => state.HolidaysReducer.holidays,
  );
  const { showHolidays, selectedStartDate, selectedEndDate } = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer,
  );

  const { selectedLanguage } = useSelector(
    (state: RootStateOrAny) => state.AppReducer.selectedLanguage,
  );

  /** Applies holidays markers on range calendar */
  const createHolidayMarkers = () => {
    holidays.forEach(element => {
      const dateSelected: any = document.querySelectorAll(
        `[data-date="${element.date}"]`,
      );
      if (dateSelected[0]) {
        createHolidayEvent(
          element.date,
          dateSelected[0],
          t(`app.common.holydays.${element.alias_name}`, element.holiday),
        );
      }
    });
  };
  /** When showHolidays toggle off removes markers from the calendar */
  const removeHolidayMarkers = () => {
    const holidaysSelected = document.querySelectorAll(
      `[data-type-holiday-event]`,
    );
    removeEventListenersForToolTip();
    if (holidaysSelected[0]) {
      holidaysSelected.forEach(element => {
        element.removeAttribute('data-type-holiday-event');
        element.querySelector('[data-type-holiday="Holiday"]')?.remove();
      });
    }
    removeHolidayToolTip();
  };

  /** Handles date changes */
  const onSelectDate = e => {
    if (e.eventType === 3) {
      const startDate = moment(e.start).startOf('day');
      const endDate = moment(e.end).startOf('day');

      dispatch(setSelectedRangeDate(startDate, endDate));
    }
  };

  const updateHolidayMarkers = () => {
    removeHolidayMarkers();
    if (showHolidays) {
      createHolidayMarkers();
    }
  };

  const debouncedUpdateHolidays = React.useCallback(
    debounce(() => {
      updateHolidayMarkers();
    }, 300),
    [],
  );

  const scrollTimeOut = () => {
    debouncedUpdateHolidays();
  };

  const timeZone = getAdjustedTimeZone({ RestaurantId, TimeZone });

  useEffect(() => {
    updateHolidayMarkers();
  }, [holidays, showHolidays]);

  useEffect(() => {
    return () => {
      // removeEventListenersForToolTip();
      updateHolidayMarkers();
    };
  });

  const { isMDSUpAndRunning } = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer,
  );
  const { RestaurantStatus } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );
  const { ramWarningBarToggle } = useFlags();

  const isRestaurantClosed = ['Closed', 'Temp Closed'].includes(
    RestaurantStatus,
  );

  const tooltipMessage = useGeneralTooltip({
    ramWarningBarToggle,
    isMDSUpAndRunning,
    restaurantId: RestaurantId,
    isRestaurantClosed,
  });

  function shouldDisableCalendar() {
    if (!isMDSUpAndRunning) return true;
    if (RestaurantId && isRestaurantClosed) return true;
    return false;
  }

  return (
    <React.Fragment>
      <Paper className={classes.paperCalendar}>
        <Grid item xs={12}>
          <RBIFollowCursorTooltip
            title={tooltipMessage}
            disabled={shouldDisableCalendar()}
          >
            <InfiniteCalendar
              locale={{
                headerFormat: 'MMM Do',
                locale:
                  localesMap[
                    selectedLanguage ||
                      localStorage.getItem('selectedLanguage') ||
                      'en'
                  ],
                blank: t('app.common.calendar.selectADate'),
                todayLabel: {
                  long: t('app.common.calendar.today', 'today'),
                  short: t('app.common.calendar.today', 'Today'),
                },
                weekdays: [
                  t('app.common.calendar.sunday_short', 'sun'),
                  t('app.common.calendar.monday_short', 'mon'),
                  t('app.common.calendar.tuesday_short', 'tue'),
                  t('app.common.calendar.wednesday_short', 'wed'),
                  t('app.common.calendar.thursday_short', 'thu'),
                  t('app.common.calendar.friday_short', 'fri'),
                  t('app.common.calendar.saturday_short', 'sat'),
                ],
                weekStartsOn: 0,
              }}
              theme={{
                selectionColor: 'rgb(162, 162, 162)',
                headerColor: 'rgb(138, 138, 138)',
                weekdayColor: 'rgb(216, 216, 216)',
              }}
              minDate={moment.tz(timeZone).format()}
              width={'100%'}
              height={350}
              rowHeight={50}
              selected={
                selectedStartDate && selectedEndDate
                  ? {
                      start: selectedStartDate.format('YYYY-MM-DD'),
                      end: selectedEndDate.format('YYYY-MM-DD'),
                    }
                  : {
                      start: today,
                      end: today,
                    }
              }
              Component={withRange(Calendar)}
              onSelect={onSelectDate}
              onScroll={scrollTimeOut}
            />
          </RBIFollowCursorTooltip>
        </Grid>
      </Paper>
      <AppBar
        elevation={1}
        position="static"
        style={{ backgroundColor: 'white' }}
      >
        {RestaurantId && (
          <Toolbar variant="dense">
            <Grid container className={classes.calendarFooter}>
              <Grid item xs={9}>
                <Typography className={classes.holidayTypography}>
                  {t('operationHours.displayHolidays')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Switch
                  className={classes.holidaySwitch}
                  name="checkedHolidays"
                  color="default"
                  checked={showHolidays}
                  onChange={() => dispatch(showHolidaysAction(!showHolidays))}
                />
              </Grid>
            </Grid>
          </Toolbar>
        )}
      </AppBar>
    </React.Fragment>
  );
}

function getAdjustedTimeZone({ RestaurantId, TimeZone }) {
  return RestaurantId ? TimeZone || 'UTC' : 'Pacific/Midway';
}
